import * as THREE from 'three';
import {
  addSurfaceRestPositionAttribute,
  INTERIOR_TILE_METERS,
  restToInteriorUv,
  SURFACE_REST_POSITION_ATTRIBUTE,
} from '../../services/fracture/cellGeometry';
import type { SurfaceDefinition, SurfaceFace } from './SurfaceDefinition';
import {
  BAKE_OUTPUT_ALBEDO,
  BAKE_OUTPUT_NORMAL,
  BAKE_OUTPUT_OFF,
  BAKE_OUTPUT_ROUGHNESS,
  setBakeOutput,
} from './SurfaceBakeChunks';

export type BakeFaceId = 'front' | 'right' | 'top';

export interface BakeFaceMaps {
  id: BakeFaceId;
  /** BoxGeometry material group index. */
  group: number;
  width: number;
  height: number;
  albedo: THREE.Texture;
  roughness: THREE.Texture;
  normal: THREE.Texture;
  albedoTarget?: THREE.WebGLRenderTarget;
  roughnessTarget?: THREE.WebGLRenderTarget;
  normalTarget?: THREE.WebGLRenderTarget;
}

export interface BakedSurfaceMaps {
  size: number;
  faces: Record<BakeFaceId, BakeFaceMaps>;
  textures: THREE.Texture[];
  targets: THREE.WebGLRenderTarget[];
  dispose(): void;
}

/** Front face long-axis pixels vs side/top. Texel aspect matches the face. */
export const BAKE_FRONT_SIZE = 2048;
export const BAKE_SIDE_SIZE = 1024;

export type BakeSizeInput = number | { front: number; side?: number };

export interface BakeSamplingOptions {
  generateMipmaps?: boolean;
  /** Cap; actual anisotropy is min(cap, renderer.max). Default 8. */
  anisotropy?: number;
  /**
   * Keep GPU render-target textures (no CPU readback). Live raster uses these
   * with hardware mipmaps. Pathtracer still needs canvas images — call
   * `copyBakedMapsToCanvas`.
   */
  keepRenderTargets?: boolean;
}

export function evenDim(n: number): number {
  const rounded = Math.max(2, Math.round(n));
  return rounded + (rounded % 2);
}

/** Pixel size for one face so texel aspect equals the world face aspect. */
export function faceBakeResolution(
  layout: { id: BakeFaceId; halfU: FaceLayout['halfU']; halfV: FaceLayout['halfV'] },
  wall: { width: number; height: number; thickness: number },
  longAxisPixels: number,
): { width: number; height: number } {
  const u = 2 * layout.halfU(wall.width, wall.height, wall.thickness);
  const v = 2 * layout.halfV(wall.width, wall.height, wall.thickness);
  const long = Math.max(u, v, 1e-6);
  const scale = Math.max(2, longAxisPixels) / long;
  return { width: evenDim(u * scale), height: evenDim(v * scale) };
}

function longAxisPixels(size: BakeSizeInput, id: BakeFaceId): number {
  if (typeof size === 'number') return Math.max(2, Math.floor(size));
  if (id === 'front') return Math.max(2, Math.floor(size.front));
  return Math.max(2, Math.floor(size.side ?? size.front));
}

export interface BakeWallSpec {
  width: number;
  height: number;
  thickness: number;
  /** World-space center of the box mesh. */
  position: THREE.Vector3;
  mesh?: THREE.Mesh;
}

export interface FaceLayout {
  id: BakeFaceId;
  group: number;
  normal: THREE.Vector3;
  tangent: THREE.Vector3;
  bitangent: THREE.Vector3;
  halfU: (w: number, h: number, t: number) => number;
  halfV: (w: number, h: number, t: number) => number;
  halfN: (w: number, h: number, t: number) => number;
}

/** Three visible slab faces. Basis matches BoxGeometry UV (u along tangent, v along bitangent). */
export const BAKE_FACE_LAYOUTS: FaceLayout[] = [
  {
    id: 'front',
    group: 4,
    normal: new THREE.Vector3(0, 0, 1),
    tangent: new THREE.Vector3(1, 0, 0),
    bitangent: new THREE.Vector3(0, 1, 0),
    halfU: (w) => w / 2,
    halfV: (_w, h) => h / 2,
    halfN: (_w, _h, t) => t / 2,
  },
  {
    id: 'right',
    group: 0,
    normal: new THREE.Vector3(1, 0, 0),
    tangent: new THREE.Vector3(0, 0, -1),
    bitangent: new THREE.Vector3(0, 1, 0),
    halfU: (_w, _h, t) => t / 2,
    halfV: (_w, h) => h / 2,
    halfN: (w) => w / 2,
  },
  {
    id: 'top',
    group: 2,
    normal: new THREE.Vector3(0, 1, 0),
    tangent: new THREE.Vector3(1, 0, 0),
    bitangent: new THREE.Vector3(0, 0, -1),
    halfU: (w) => w / 2,
    halfV: (_w, _h, t) => t / 2,
    halfN: (_w, h) => h / 2,
  },
];

function makeTarget(
  width: number,
  height: number,
  colorSpace: THREE.ColorSpace,
  sampling?: { mipmaps: boolean; anisotropy: number },
): THREE.WebGLRenderTarget {
  const mipmaps = sampling?.mipmaps === true;
  const rt = new THREE.WebGLRenderTarget(width, height, {
    type: THREE.UnsignedByteType,
    format: THREE.RGBAFormat,
    minFilter: mipmaps ? THREE.LinearMipmapLinearFilter : THREE.LinearFilter,
    magFilter: THREE.LinearFilter,
    generateMipmaps: mipmaps,
    colorSpace,
    depthBuffer: true,
    stencilBuffer: false,
  });
  rt.texture.wrapS = THREE.ClampToEdgeWrapping;
  rt.texture.wrapT = THREE.ClampToEdgeWrapping;
  rt.texture.colorSpace = colorSpace;
  if (sampling) rt.texture.anisotropy = sampling.anisotropy;
  return rt;
}

/** Hardware mip chain + anisotropy. Clamp wrap avoids opposite-edge bleed on 0–1 face UVs. */
export function applyBakedMapSampling(
  texture: THREE.Texture,
  renderer: THREE.WebGLRenderer,
  anisotropyCap = 8,
): void {
  const maxAniso = renderer.capabilities.getMaxAnisotropy();
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.magFilter = THREE.LinearFilter;
  texture.anisotropy = Math.min(anisotropyCap, maxAniso);
  texture.wrapS = THREE.ClampToEdgeWrapping;
  texture.wrapT = THREE.ClampToEdgeWrapping;
  texture.needsUpdate = true;
}

function linearByteToSrgbByte(v: number): number {
  const x = v / 255;
  const s = x <= 0.0031308 ? 12.92 * x : 1.055 * x ** (1 / 2.4) - 0.055;
  return Math.min(255, Math.max(0, Math.round(s * 255)));
}

/**
 * Pathtracer packs `texture.image` via a blit, so RT textures (no image) are
 * invisible. Copy to a CanvasTexture with the same wrap/filter as the tracer's
 * texture array (Repeat + Linear, no mips).
 */
function copyTargetToCanvasTexture(
  renderer: THREE.WebGLRenderer,
  rt: THREE.WebGLRenderTarget,
  colorSpace: THREE.ColorSpace,
  encodeSrgb: boolean,
): THREE.CanvasTexture {
  const w = rt.width;
  const h = rt.height;
  const src = new Uint8Array(w * h * 4);
  renderer.readRenderTargetPixels(rt, 0, 0, w, h, src);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('SurfaceBaker: 2d context unavailable');
  const img = ctx.createImageData(w, h);
  for (let y = 0; y < h; y += 1) {
    const srcOff = (h - 1 - y) * w * 4;
    const dstOff = y * w * 4;
    for (let x = 0; x < w; x += 1) {
      const i = x * 4;
      let r = src[srcOff + i];
      let g = src[srcOff + i + 1];
      let b = src[srcOff + i + 2];
      if (encodeSrgb) {
        r = linearByteToSrgbByte(r);
        g = linearByteToSrgbByte(g);
        b = linearByteToSrgbByte(b);
      }
      img.data[dstOff + i] = r;
      img.data[dstOff + i + 1] = g;
      img.data[dstOff + i + 2] = b;
      img.data[dstOff + i + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = colorSpace;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.generateMipmaps = false;
  tex.flipY = true;
  tex.needsUpdate = true;
  return tex;
}

export function placeFaceCamera(
  mesh: THREE.Mesh,
  layout: FaceLayout,
  wall: BakeWallSpec,
): THREE.OrthographicCamera {
  const { width: w, height: h, thickness: t } = wall;
  const halfU = layout.halfU(w, h, t);
  const halfV = layout.halfV(w, h, t);
  const halfN = layout.halfN(w, h, t);
  const localCenter = layout.normal.clone().multiplyScalar(halfN);
  const worldCenter = localCenter.clone();
  mesh.localToWorld(worldCenter);
  const worldN = layout.normal.clone().transformDirection(mesh.matrixWorld).normalize();
  const worldUp = layout.bitangent.clone().transformDirection(mesh.matrixWorld).normalize();
  const cam = new THREE.OrthographicCamera(-halfU, halfU, halfV, -halfV, 0.002, 0.35);
  cam.up.copy(worldUp);
  cam.position.copy(worldCenter).addScaledVector(worldN, 0.04);
  cam.lookAt(worldCenter);
  cam.updateProjectionMatrix();
  cam.updateMatrixWorld(true);
  return cam;
}

function worldBasis(mesh: THREE.Mesh, layout: FaceLayout) {
  return {
    tangent: layout.tangent.clone().transformDirection(mesh.matrixWorld).normalize(),
    bitangent: layout.bitangent.clone().transformDirection(mesh.matrixWorld).normalize(),
    normal: layout.normal.clone().transformDirection(mesh.matrixWorld).normalize(),
  };
}

/**
 * Raster the live procedural material's albedo / roughness / tangent-space
 * normal onto the three visible wall faces. Uses the real mesh transform so
 * world-space triplanar patterns match the raster view.
 */
export function bakeSurfaceMaps(
  renderer: THREE.WebGLRenderer,
  definition: SurfaceDefinition,
  settings: unknown,
  face: SurfaceFace,
  size: BakeSizeInput,
  wall: BakeWallSpec,
  sampling?: BakeSamplingOptions,
): BakedSurfaceMaps {
  const bakeSize = typeof size === 'number' ? Math.max(16, Math.floor(size)) : Math.max(16, Math.floor(size.front));
  const params = face === 'interior' ? definition.physics.skin.interior : definition.physics.skin.outer;
  const bakeMat = definition.createMaterial(params, settings, face);
  bakeMat.side = THREE.FrontSide;
  bakeMat.dithering = false;
  bakeMat.fog = false;
  bakeMat.toneMapped = false;

  const ownMesh = !wall.mesh;
  const geom = wall.mesh
    ? (wall.mesh.geometry as THREE.BufferGeometry)
    : (() => {
        const g = new THREE.BoxGeometry(wall.width, wall.height, wall.thickness);
        addSurfaceRestPositionAttribute(g);
        return g;
      })();
  const mesh = wall.mesh ?? new THREE.Mesh(geom, bakeMat);
  const prevMat = mesh.material;
  mesh.material = bakeMat;
  if (ownMesh) {
    mesh.position.copy(wall.position);
    mesh.updateMatrixWorld(true);
  } else {
    mesh.updateWorldMatrix(true, false);
  }

  const bakeScene = new THREE.Scene();
  bakeScene.background = new THREE.Color(0, 0, 0);
  bakeScene.environment = null;
  bakeScene.fog = null;
  const parent = mesh.parent;
  bakeScene.add(mesh);

  const prevTM = renderer.toneMapping;
  const prevExp = renderer.toneMappingExposure;
  const prevCS = renderer.outputColorSpace;
  const prevTarget = renderer.getRenderTarget();
  const prevAutoClear = renderer.autoClear;
  const prevClear = new THREE.Color();
  const prevAlpha = renderer.getClearAlpha();
  renderer.getClearColor(prevClear);
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.autoClear = true;

  const textures: THREE.Texture[] = [];
  const targets: THREE.WebGLRenderTarget[] = [];
  const faces = {} as Record<BakeFaceId, BakeFaceMaps>;
  const mipmaps = sampling?.generateMipmaps === true;
  const anisoCap = sampling?.anisotropy ?? 8;
  const keepRT = sampling?.keepRenderTargets === true;
  const liveSampling = mipmaps
    ? { mipmaps: true, anisotropy: Math.min(anisoCap, renderer.capabilities.getMaxAnisotropy()) }
    : undefined;

  try {
    const firstCam = placeFaceCamera(mesh, BAKE_FACE_LAYOUTS[0], wall);
    renderer.compile(bakeScene, firstCam);

    for (const layout of BAKE_FACE_LAYOUTS) {
      const camera = layout.id === 'front' ? firstCam : placeFaceCamera(mesh, layout, wall);
      const basis = worldBasis(mesh, layout);
      const res = faceBakeResolution(layout, wall, longAxisPixels(size, layout.id));

      const albedoRT = makeTarget(res.width, res.height, THREE.LinearSRGBColorSpace, liveSampling);
      const roughnessRT = makeTarget(res.width, res.height, THREE.NoColorSpace, liveSampling);
      const normalRT = makeTarget(res.width, res.height, THREE.NoColorSpace, liveSampling);

      const passes: Array<{
        mode: number;
        rt: THREE.WebGLRenderTarget;
        space: THREE.ColorSpace;
        encodeSrgb: boolean;
      }> = [
        { mode: BAKE_OUTPUT_ALBEDO, rt: albedoRT, space: THREE.SRGBColorSpace, encodeSrgb: true },
        { mode: BAKE_OUTPUT_ROUGHNESS, rt: roughnessRT, space: THREE.NoColorSpace, encodeSrgb: false },
        { mode: BAKE_OUTPUT_NORMAL, rt: normalRT, space: THREE.NoColorSpace, encodeSrgb: false },
      ];
      const mapsOut: THREE.Texture[] = [];
      for (const pass of passes) {
        setBakeOutput(bakeMat, pass.mode, basis);
        renderer.setRenderTarget(pass.rt);
        renderer.setClearColor(0x000000, 1);
        renderer.clear();
        renderer.render(bakeScene, camera);
        if (keepRT) {
          mapsOut.push(pass.rt.texture);
        } else {
          const tex = copyTargetToCanvasTexture(renderer, pass.rt, pass.space, pass.encodeSrgb);
          if (mipmaps) applyBakedMapSampling(tex, renderer, anisoCap);
          mapsOut.push(tex);
          pass.rt.dispose();
        }
      }
      setBakeOutput(bakeMat, BAKE_OUTPUT_OFF);
      textures.push(...mapsOut);
      if (keepRT) targets.push(albedoRT, roughnessRT, normalRT);

      faces[layout.id] = {
        id: layout.id,
        group: layout.group,
        width: res.width,
        height: res.height,
        albedo: mapsOut[0],
        roughness: mapsOut[1],
        normal: mapsOut[2],
        albedoTarget: keepRT ? albedoRT : undefined,
        roughnessTarget: keepRT ? roughnessRT : undefined,
        normalTarget: keepRT ? normalRT : undefined,
      };
    }
  } finally {
    renderer.setRenderTarget(prevTarget);
    renderer.toneMapping = prevTM;
    renderer.toneMappingExposure = prevExp;
    renderer.outputColorSpace = prevCS;
    renderer.autoClear = prevAutoClear;
    renderer.setClearColor(prevClear, prevAlpha);
    if (parent) parent.add(mesh);
    else bakeScene.remove(mesh);
    mesh.material = prevMat;
    if (ownMesh) {
      geom.dispose();
    }
    bakeMat.dispose();
  }

  return {
    size: bakeSize,
    faces,
    textures,
    targets,
    dispose() {
      if (targets.length > 0) {
        for (const rt of targets) rt.dispose();
      } else {
        for (const tex of textures) tex.dispose();
      }
    },
  };
}

/** Pathtracer-facing canvas copies of a keepRenderTargets bake. */
export function copyBakedMapsToCanvas(
  renderer: THREE.WebGLRenderer,
  maps: BakedSurfaceMaps,
): BakedSurfaceMaps {
  if (maps.targets.length === 0) return maps;
  const textures: THREE.Texture[] = [];
  const faces = {} as Record<BakeFaceId, BakeFaceMaps>;
  for (const layout of BAKE_FACE_LAYOUTS) {
    const src = maps.faces[layout.id];
    if (!src.albedoTarget || !src.roughnessTarget || !src.normalTarget) return maps;
    const albedo = copyTargetToCanvasTexture(renderer, src.albedoTarget, THREE.SRGBColorSpace, true);
    const roughness = copyTargetToCanvasTexture(renderer, src.roughnessTarget, THREE.NoColorSpace, false);
    const normal = copyTargetToCanvasTexture(renderer, src.normalTarget, THREE.NoColorSpace, false);
    textures.push(albedo, roughness, normal);
    faces[layout.id] = {
      id: layout.id,
      group: src.group,
      width: src.width,
      height: src.height,
      albedo,
      roughness,
      normal,
    };
  }
  return {
    size: maps.size,
    faces,
    textures,
    targets: [],
    dispose() {
      for (const tex of textures) tex.dispose();
    },
  };
}

export interface BakedPreviewOptions {
  clearcoat?: number;
  clearcoatRoughness?: number;
}

/** Six MeshPhysicalMaterials for BoxGeometry groups, driven by the baked maps. */
export function createBakedPreviewMaterials(
  baked: BakedSurfaceMaps,
  options: BakedPreviewOptions = {},
): THREE.MeshPhysicalMaterial[] {
  const byGroup: Record<number, BakeFaceMaps> = {
    0: baked.faces.right,
    1: baked.faces.right,
    2: baked.faces.top,
    3: baked.faces.top,
    4: baked.faces.front,
    5: baked.faces.front,
  };
  return [0, 1, 2, 3, 4, 5].map((group) => {
    const maps = byGroup[group];
    const mat = new THREE.MeshPhysicalMaterial({
      color: 0xffffff,
      map: maps.albedo,
      roughnessMap: maps.roughness,
      roughness: 1,
      metalness: 0,
      normalMap: maps.normal,
      normalScale: new THREE.Vector2(1, 1),
      envMapIntensity: 1,
      vertexColors: false,
      flatShading: false,
      clearcoat: options.clearcoat ?? 0,
      clearcoatRoughness: options.clearcoatRoughness ?? 1,
      dithering: false,
    });
    mat.toneMapped = true;
    return mat;
  });
}

/**
 * Compact one indexed group into its own geometry (unique verts only).
 * Preserves the source UVs — those already match the bake cameras.
 */
export function extractIndexedGroup(
  src: THREE.BufferGeometry,
  group: { start: number; count: number },
): THREE.BufferGeometry {
  const srcIndex = src.index;
  if (!srcIndex) throw new Error('extractIndexedGroup: geometry is not indexed');
  const used: number[] = [];
  const remap = new Map<number, number>();
  for (let i = 0; i < group.count; i += 1) {
    const old = srcIndex.getX(group.start + i);
    let next = remap.get(old);
    if (next === undefined) {
      next = used.length;
      remap.set(old, next);
      used.push(old);
    }
  }
  const geom = new THREE.BufferGeometry();
  for (const name of Object.keys(src.attributes)) {
    const attr = src.getAttribute(name);
    const itemSize = attr.itemSize;
    const srcArray = attr.array;
    const array = new Float32Array(used.length * itemSize);
    for (let i = 0; i < used.length; i += 1) {
      const srcOff = used[i] * itemSize;
      const dstOff = i * itemSize;
      for (let k = 0; k < itemSize; k += 1) {
        array[dstOff + k] = srcArray[srcOff + k];
      }
    }
    geom.setAttribute(name, new THREE.BufferAttribute(array, itemSize, attr.normalized));
  }
  const index = new Uint16Array(group.count);
  for (let i = 0; i < group.count; i += 1) {
    index[i] = remap.get(srcIndex.getX(group.start + i)) ?? 0;
  }
  geom.setIndex(new THREE.BufferAttribute(index, 1));
  return geom;
}

/**
 * One mesh per BoxGeometry face, each with a single material.
 *
 * three-gpu-pathtracer merges meshes and then assigns `materials[meshIndex]`
 * from a flattened material list. A 6-slot array on ONE box therefore paints
 * every face with `materials[0]` (the +X / thickness×height bake). The front
 * 6 m face then samples a ~0.3–0.6 m capture — horizontal planks, one
 * squashed vertical strip of head joints. Raster WebGL honors groups, which
 * is why bake-preview looked fine on the same maps.
 */
export function createBakedPreviewFaceMeshes(
  baked: BakedSurfaceMaps,
  wall: BakeWallSpec,
  options: BakedPreviewOptions = {},
): { meshes: THREE.Mesh[]; materials: THREE.MeshPhysicalMaterial[] } {
  const materials = createBakedPreviewMaterials(baked, options);
  const src = new THREE.BoxGeometry(wall.width, wall.height, wall.thickness);
  addSurfaceRestPositionAttribute(src);
  src.computeTangents();
  const meshes: THREE.Mesh[] = [];
  for (let i = 0; i < src.groups.length; i += 1) {
    const geom = extractIndexedGroup(src, src.groups[i]);
    const mesh = new THREE.Mesh(geom, materials[i] ?? materials[0]);
    mesh.position.copy(wall.position);
    mesh.castShadow = true;
    mesh.receiveShadow = true;
    meshes.push(mesh);
  }
  src.dispose();
  return { meshes, materials };
}

export function disposeBakedPreviewMaterials(materials: THREE.Material[]): void {
  for (const mat of materials) mat.dispose();
}

export { INTERIOR_TILE_METERS };
export const INTERIOR_BAKE_SIZE = 1024;
export const ATLAS_PAD_PX = 32;
export const WRAP_BLEND_PX = 48;

export interface AtlasRegion {
  id: BakeFaceId;
  u0: number;
  v0: number;
  u1: number;
  v1: number;
}

export interface BakedAtlasMaps {
  width: number;
  height: number;
  albedo: THREE.Texture;
  roughness: THREE.Texture;
  normal: THREE.Texture;
  regions: Record<BakeFaceId, AtlasRegion>;
  textures: THREE.Texture[];
  targets: THREE.WebGLRenderTarget[];
  dispose(): void;
}

export interface BakedTileMaps {
  size: number;
  tileMeters: number;
  albedo: THREE.Texture;
  roughness: THREE.Texture;
  normal: THREE.Texture;
  textures: THREE.Texture[];
  targets: THREE.WebGLRenderTarget[];
  dispose(): void;
}

const BLIT_VS = /* glsl */ `
varying vec2 vUv;
void main() {
  vUv = uv;
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const BLIT_FS = /* glsl */ `
uniform sampler2D tSrc;
varying vec2 vUv;
void main() {
  gl_FragColor = texture2D(tSrc, vUv);
}
`;

const WRAP_BLEND_FS = /* glsl */ `
uniform sampler2D tSrc;
uniform float blendUv;
varying vec2 vUv;
void main() {
  vec4 center = texture2D(tSrc, vUv);
  float w = smoothstep(0.0, blendUv, vUv.x) * smoothstep(0.0, blendUv, 1.0 - vUv.x)
    * smoothstep(0.0, blendUv, vUv.y) * smoothstep(0.0, blendUv, 1.0 - vUv.y);
  vec4 wrapU = texture2D(tSrc, vec2(fract(vUv.x + 0.5), vUv.y));
  vec4 wrapV = texture2D(tSrc, vec2(vUv.x, fract(vUv.y + 0.5)));
  vec4 wrap = mix(wrapU, wrapV, 0.5);
  gl_FragColor = mix(wrap, center, w);
}
`;

interface RendererSnapshot {
  tm: THREE.ToneMapping;
  exp: number;
  cs: THREE.ColorSpace;
  target: THREE.WebGLRenderTarget | null;
  autoClear: boolean;
  clear: THREE.Color;
  alpha: number;
  viewport: THREE.Vector4;
  scissor: THREE.Vector4;
  scissorTest: boolean;
}

function snapshotRenderer(renderer: THREE.WebGLRenderer): RendererSnapshot {
  const clear = new THREE.Color();
  renderer.getClearColor(clear);
  return {
    tm: renderer.toneMapping,
    exp: renderer.toneMappingExposure,
    cs: renderer.outputColorSpace as THREE.ColorSpace,
    target: renderer.getRenderTarget(),
    autoClear: renderer.autoClear,
    clear,
    alpha: renderer.getClearAlpha(),
    viewport: renderer.getViewport(new THREE.Vector4()),
    scissor: renderer.getScissor(new THREE.Vector4()),
    scissorTest: renderer.getScissorTest(),
  };
}

function restoreRenderer(renderer: THREE.WebGLRenderer, snap: RendererSnapshot): void {
  renderer.setRenderTarget(snap.target);
  renderer.toneMapping = snap.tm;
  renderer.toneMappingExposure = snap.exp;
  renderer.outputColorSpace = snap.cs;
  renderer.autoClear = snap.autoClear;
  renderer.setClearColor(snap.clear, snap.alpha);
  renderer.setViewport(snap.viewport);
  renderer.setScissor(snap.scissor);
  renderer.setScissorTest(snap.scissorTest);
}

function makeBlitScene(fragmentShader: string, uniforms: Record<string, THREE.IUniform>) {
  const mat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: BLIT_VS,
    fragmentShader,
    depthTest: false,
    depthWrite: false,
    toneMapped: false,
  });
  const mesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), mat);
  const scene = new THREE.Scene();
  scene.add(mesh);
  const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
  return {
    scene,
    camera,
    mat,
    dispose() {
      mesh.geometry.dispose();
      mat.dispose();
    },
  };
}

/**
 * Copy `src` into a region of `dst` (pixel origin bottom-left, matching WebGL).
 */
function blitToRegion(
  renderer: THREE.WebGLRenderer,
  src: THREE.Texture,
  dst: THREE.WebGLRenderTarget,
  x: number,
  y: number,
  width: number,
  height: number,
): void {
  const blit = makeBlitScene(BLIT_FS, { tSrc: { value: src } });
  renderer.setRenderTarget(dst);
  renderer.setViewport(x, y, width, height);
  renderer.setScissor(x, y, width, height);
  renderer.setScissorTest(true);
  renderer.autoClear = false;
  renderer.render(blit.scene, blit.camera);
  blit.dispose();
}

/**
 * Pack the three unique face bakes into one atlas without stretching.
 * Pad between regions so mips do not bleed across box faces.
 */
export function packBakedFacesToAtlas(
  renderer: THREE.WebGLRenderer,
  maps: BakedSurfaceMaps,
  pad = ATLAS_PAD_PX,
): BakedAtlasMaps {
  const front = maps.faces.front;
  const right = maps.faces.right;
  const top = maps.faces.top;
  const width = evenDim(pad + front.width + pad + right.width + pad);
  const row1 = Math.max(front.height, right.height);
  const height = evenDim(pad + row1 + pad + top.height + pad);
  const aniso = front.albedo.anisotropy || 8;
  const liveSampling = { mipmaps: true, anisotropy: aniso };

  const albedoRT = makeTarget(width, height, THREE.LinearSRGBColorSpace, liveSampling);
  const roughnessRT = makeTarget(width, height, THREE.NoColorSpace, liveSampling);
  const normalRT = makeTarget(width, height, THREE.NoColorSpace, liveSampling);

  const fx = pad;
  const fy = pad;
  const rx = pad + front.width + pad;
  const ry = pad;
  const tx = pad;
  const ty = pad + row1 + pad;

  const snap = snapshotRenderer(renderer);
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  try {
    renderer.setScissorTest(false);
    renderer.setClearColor(0x000000, 1);
    renderer.autoClear = true;
    renderer.setRenderTarget(albedoRT);
    renderer.setViewport(0, 0, width, height);
    renderer.clear();
    renderer.setRenderTarget(roughnessRT);
    renderer.setViewport(0, 0, width, height);
    renderer.clear();
    renderer.setRenderTarget(normalRT);
    renderer.setViewport(0, 0, width, height);
    renderer.clear();

    blitToRegion(renderer, front.albedo, albedoRT, fx, fy, front.width, front.height);
    blitToRegion(renderer, right.albedo, albedoRT, rx, ry, right.width, right.height);
    blitToRegion(renderer, top.albedo, albedoRT, tx, ty, top.width, top.height);

    blitToRegion(renderer, front.roughness, roughnessRT, fx, fy, front.width, front.height);
    blitToRegion(renderer, right.roughness, roughnessRT, rx, ry, right.width, right.height);
    blitToRegion(renderer, top.roughness, roughnessRT, tx, ty, top.width, top.height);

    blitToRegion(renderer, front.normal, normalRT, fx, fy, front.width, front.height);
    blitToRegion(renderer, right.normal, normalRT, rx, ry, right.width, right.height);
    blitToRegion(renderer, top.normal, normalRT, tx, ty, top.width, top.height);
  } finally {
    restoreRenderer(renderer, snap);
  }

  const textures = [albedoRT.texture, roughnessRT.texture, normalRT.texture];
  const targets = [albedoRT, roughnessRT, normalRT];
  for (const tex of textures) {
    tex.wrapS = THREE.ClampToEdgeWrapping;
    tex.wrapT = THREE.ClampToEdgeWrapping;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = aniso;
    tex.needsUpdate = true;
  }
  albedoRT.texture.colorSpace = THREE.LinearSRGBColorSpace;

  const region = (id: BakeFaceId, x: number, y: number, w: number, h: number): AtlasRegion => ({
    id,
    u0: x / width,
    v0: y / height,
    u1: (x + w) / width,
    v1: (y + h) / height,
  });

  return {
    width,
    height,
    albedo: albedoRT.texture,
    roughness: roughnessRT.texture,
    normal: normalRT.texture,
    regions: {
      front: region('front', fx, fy, front.width, front.height),
      right: region('right', rx, ry, right.width, right.height),
      top: region('top', tx, ty, top.width, top.height),
    },
    textures,
    targets,
    dispose() {
      for (const rt of targets) rt.dispose();
    },
  };
}

export function atlasUvFromFace(
  faceU: number,
  faceV: number,
  region: AtlasRegion,
): [number, number] {
  const u = THREE.MathUtils.clamp(faceU, 0, 1);
  const v = THREE.MathUtils.clamp(faceV, 0, 1);
  return [region.u0 + (region.u1 - region.u0) * u, region.v0 + (region.v1 - region.v0) * v];
}

/**
 * Bake an interior (or other isotropic) pattern onto a tileable 1024² plane.
 * Wrap-blend at the borders so Repeat sampling does not show a hard seam.
 */
export function bakeTileablePlanarMaps(
  renderer: THREE.WebGLRenderer,
  definition: SurfaceDefinition,
  settings: unknown,
  face: SurfaceFace,
  size = INTERIOR_BAKE_SIZE,
  tileMeters = INTERIOR_TILE_METERS,
  sampling?: BakeSamplingOptions,
): BakedTileMaps {
  const dim = evenDim(Math.max(16, Math.floor(size)));
  const params = face === 'interior' ? definition.physics.skin.interior : definition.physics.skin.outer;
  const bakeMat = definition.createMaterial(params, settings, face);
  bakeMat.side = THREE.FrontSide;
  bakeMat.dithering = false;
  bakeMat.fog = false;
  bakeMat.toneMapped = false;

  const geom = new THREE.PlaneGeometry(tileMeters, tileMeters, 1, 1);
  addSurfaceRestPositionAttribute(geom);
  const mesh = new THREE.Mesh(geom, bakeMat);
  mesh.updateMatrixWorld(true);

  const bakeScene = new THREE.Scene();
  bakeScene.background = new THREE.Color(0, 0, 0);
  bakeScene.environment = null;
  bakeScene.fog = null;
  bakeScene.add(mesh);

  const half = tileMeters / 2;
  const camera = new THREE.OrthographicCamera(-half, half, half, -half, 0.002, 0.35);
  camera.position.set(0, 0, 0.04);
  camera.up.set(0, 1, 0);
  camera.lookAt(0, 0, 0);
  camera.updateProjectionMatrix();
  camera.updateMatrixWorld(true);

  const basis = {
    tangent: new THREE.Vector3(1, 0, 0),
    bitangent: new THREE.Vector3(0, 1, 0),
    normal: new THREE.Vector3(0, 0, 1),
  };

  const mipmaps = sampling?.generateMipmaps === true;
  const anisoCap = sampling?.anisotropy ?? 8;
  const aniso = Math.min(anisoCap, renderer.capabilities.getMaxAnisotropy());
  const liveSampling = mipmaps ? { mipmaps: true, anisotropy: aniso } : undefined;
  const blendUv = WRAP_BLEND_PX / dim;

  const snap = snapshotRenderer(renderer);
  renderer.toneMapping = THREE.NoToneMapping;
  renderer.toneMappingExposure = 1;
  renderer.outputColorSpace = THREE.LinearSRGBColorSpace;
  renderer.autoClear = true;

  const textures: THREE.Texture[] = [];
  const targets: THREE.WebGLRenderTarget[] = [];

  try {
    renderer.compile(bakeScene, camera);
    const passes: Array<{ mode: number; space: THREE.ColorSpace }> = [
      { mode: BAKE_OUTPUT_ALBEDO, space: THREE.LinearSRGBColorSpace },
      { mode: BAKE_OUTPUT_ROUGHNESS, space: THREE.NoColorSpace },
      { mode: BAKE_OUTPUT_NORMAL, space: THREE.NoColorSpace },
    ];
    for (const pass of passes) {
      const src = makeTarget(dim, dim, pass.space, undefined);
      setBakeOutput(bakeMat, pass.mode, basis);
      renderer.setRenderTarget(src);
      renderer.setClearColor(0x000000, 1);
      renderer.clear();
      renderer.render(bakeScene, camera);

      const dst = makeTarget(dim, dim, pass.space, liveSampling);
      dst.texture.wrapS = THREE.RepeatWrapping;
      dst.texture.wrapT = THREE.RepeatWrapping;
      const blit = makeBlitScene(WRAP_BLEND_FS, {
        tSrc: { value: src.texture },
        blendUv: { value: blendUv },
      });
      renderer.setRenderTarget(dst);
      renderer.setScissorTest(false);
      renderer.setViewport(0, 0, dim, dim);
      renderer.autoClear = true;
      renderer.clear();
      renderer.render(blit.scene, blit.camera);
      blit.dispose();
      src.dispose();

      textures.push(dst.texture);
      targets.push(dst);
    }
    setBakeOutput(bakeMat, BAKE_OUTPUT_OFF);
  } finally {
    restoreRenderer(renderer, snap);
    bakeScene.remove(mesh);
    geom.dispose();
    bakeMat.dispose();
  }

  for (const tex of textures) {
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.generateMipmaps = true;
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = aniso;
    tex.needsUpdate = true;
  }

  return {
    size: dim,
    tileMeters,
    albedo: textures[0],
    roughness: textures[1],
    normal: textures[2],
    textures,
    targets,
    dispose() {
      for (const rt of targets) rt.dispose();
    },
  };
}

/**
 * Rest-space UVs so a shared tileable bake repeats at `tileMeters` on any box
 * size. Face tangent follows the same projection as interior cell UVs.
 */
export function assignTileableBoxUvs(
  geometry: THREE.BufferGeometry,
  tileMeters = INTERIOR_TILE_METERS,
): void {
  const position = geometry.getAttribute('position');
  const normal = geometry.getAttribute('normal');
  if (!position || position.itemSize < 3) {
    throw new TypeError('assignTileableBoxUvs requires a position attribute with xyz coordinates');
  }
  if (!normal || normal.itemSize < 3) {
    throw new TypeError('assignTileableBoxUvs requires a normal attribute with xyz coordinates');
  }
  const rest = geometry.getAttribute(SURFACE_REST_POSITION_ATTRIBUTE) ?? position;
  const uv = new Float32Array(position.count * 2);
  const tile = Math.max(tileMeters, 1e-6);
  for (let i = 0; i < position.count; i += 1) {
    const [u, v] = restToInteriorUv(
      rest.getX(i),
      rest.getY(i),
      rest.getZ(i),
      normal.getX(i),
      normal.getY(i),
      normal.getZ(i),
      tile,
    );
    uv[i * 2] = u;
    uv[i * 2 + 1] = v;
  }
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
}
